import { auth } from '../../config/firebase-config';

import { RecaptchaVerifier, signInWithPhoneNumber, signInWithCredential,PhoneAuthProvider,onAuthStateChanged,signOut, getAuth, signInAnonymously} from 'firebase/auth';
import ApiService, { getApiBaseUrl } from '../api/api_service';
import { ApiException } from '../../utils/exceptions';
import SessionService from '../session_service';


export function initAuthObserver(callback) {
  const auth = getAuth();
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      // If no user, sign in anonymously
      await signInAnonymously(auth);
      // onAuthStateChanged will fire again after sign-in
      return;
    }
    
    // Send session data when auth state changes (user becomes available)
    try {
      await SessionService.sendLoginSession(user);
    } catch (error) {
      console.warn('Error sending session data on auth state change:', error);
    }
    
    callback(user);
  });
}




// the first function that will be called when the user clicks on the send OTP button
// if did not throw any error, it will return the verificationId front end should open otp modal and send the
// entered otp to the verifyOtp function
// expected responses are: 
// 1. verificationId: string
// 2. FirebaseError: object error.message will contain the the message
export const sendOtp = async (phoneNumber) => {

  try {
    // if captcha is not initialized, initialize it 
    if(!window.recaptchaVerifier){
        window.recaptchaVerifier = new RecaptchaVerifier(auth,'recaptcha-container', {
            size: 'invisible',
            callback: (response) => {
              // Handle successful reCAPTCHA verification here
              console.log('reCAPTCHA verified successfully');
            },
            'expired-callback': () => {
              // Handle reCAPTCHA expiration here
              console.log('reCAPTCHA expired, please retry');
            }
            });
            window.recaptchaWidgetId = window.recaptchaVerifier.render().then(widgetId => {
              window.recaptchaWidgetId = widgetId;
            }).catch(error => {
              console.error('Error rendering reCAPTCHA:', error);
            });
    }
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, window.recaptchaVerifier);
    return confirmationResult.verificationId;
  } catch (error) {
    const errorMessage = handleFirebaseError(error);
    throw new Error(errorMessage);
  }
};

// the second function that will be called when the user clicks on the verify OTP button
// if did not throw any error, it will return the token string which can be used to authenticate the user
// expected responses are:
// 1. token: string
// 2. FirebaseError: object error.message will contain the the message

export const verifyOtp = async (verificationId, otp) => {
  try {
    // Verify OTP

    const authCredential = await PhoneAuthProvider.credential(verificationId, otp);
    const userCredential = await signInWithCredential(auth,authCredential);
    
    // Send login session data after successful authentication
    await SessionService.sendLoginSession(userCredential.user);

    return userCredential.user.accessToken
  }  catch (error) {
    const errorMessage = handleFirebaseError(error);
    throw new Error(errorMessage);
  }
};

export const signOutUser = async () => {
  try {
    const currentUser = auth.currentUser;
    
    // Send logout session data before signing out
    if (currentUser) {
      await SessionService.sendLogoutSession(currentUser);
    }
    
    await signOut(auth);
  } catch (error) {
    const errorMessage = handleFirebaseError(error);
    throw new Error(errorMessage);
  }
};

export const exchangeToken = async ({dynamicState,auth}) => {
  const path = '/v1/auth/exchange';
  const headers = {
      'authorization': `Bearer ${auth.user.accessToken}`,
      'contentType':"application/json"
  };
  const data = {}
  const options = {"dynamicState":dynamicState,"reformatData":false}

try {

  const response = await ApiService.post(path,headers,data,options);
  const token = response.data; 
  return token
  
  } catch(error) {
      throw new ApiException(error)
  }
};

// Password Login - authenticate with mobile/email and password
export const passwordLogin = async (mobileOrEmail, password) => {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v4/auth/password-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mobile_with_plus_or_email: mobileOrEmail,
        password,
      }),
    });

    if (!res.ok) {
      const errText = await res.text();
      let message = 'Invalid credentials';
      try {
        const parsed = JSON.parse(errText);
        message = parsed?.error?.message || parsed?.debug?.message || parsed?.message || message;
      } catch { /* use default */ }
      throw new Error(message);
    }

    const data = await res.json();
    const customToken = typeof data === 'string' ? data : data.token || data;
    const { signInWithCustomToken } = await import('firebase/auth');
    const result = await signInWithCustomToken(auth, String(customToken));
    
    try {
      await SessionService.sendLoginSession(result.user);
    } catch (sessionError) {
      console.warn('Error sending login session:', sessionError);
    }
    
    return result.user;
  } catch (error) {
    throw error;
  }
};

// Google Sign-In
export const googleSignIn = async () => {
  try {
    const { GoogleAuthProvider, signInWithPopup, signInWithCustomToken, linkWithCredential, signOut: firebaseSignOut } = await import('firebase/auth');
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    
    // Save Google credential for potential account linking
    const googleCredential = GoogleAuthProvider.credentialFromResult(result);
    
    // Sync with backend
    const token = await result.user.getIdToken();
    const res = await fetch(`${getApiBaseUrl()}/v4/auth/social`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        displayName: result.user.displayName || '',
        photoURL: result.user.photoURL || '',
        authProvider: 'google',
      }),
    });

    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData?.friendlyMessage || responseData?.error?.message || responseData?.message || 'Unable to complete social sign-in');
    }
    
    // Handle account linking — existing user with same email but different UID
    if (responseData.needsLinking && responseData.customToken) {
      // Sign out the temporary Google user first to prevent
      // onAuthStateChanged from triggering a profile load with the wrong UID
      await firebaseSignOut(auth);
      
      // Sign in as the original user using the custom token
      const originalResult = await signInWithCustomToken(auth, responseData.customToken);
      
      // Link Google provider to the original account
      if (googleCredential) {
        try {
          await linkWithCredential(originalResult.user, googleCredential);
        } catch (linkErr) {
          // auth/provider-already-linked means Google already linked — that's fine
          // auth/credential-already-in-use can happen if deletion hasn't propagated — ignore
          console.warn('Could not link Google credential:', linkErr.code || linkErr.message);
        }
      }
      
      try {
        await SessionService.sendLoginSession(originalResult.user);
      } catch (sessionError) {
        console.warn('Error sending login session:', sessionError);
      }
      
      return originalResult.user;
    }
    
    // Normal flow — no linking needed
    try {
      await SessionService.sendLoginSession(result.user);
    } catch (sessionError) {
      console.warn('Error sending login session:', sessionError);
    }
    
    return result.user;
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      return null; // User cancelled
    }
    throw error;
  }
};

// Apple Sign-In
export const appleSignIn = async () => {
  try {
    const { OAuthProvider, signInWithPopup, signInWithCustomToken, linkWithCredential, signOut: firebaseSignOut } = await import('firebase/auth');
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    const result = await signInWithPopup(auth, provider);
    
    // Save Apple credential for potential account linking
    const appleCredential = OAuthProvider.credentialFromResult(result);
    
    // Sync with backend
    const token = await result.user.getIdToken();
    const res = await fetch(`${getApiBaseUrl()}/v4/auth/social`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        displayName: result.user.displayName || '',
        photoURL: result.user.photoURL || '',
        authProvider: 'apple',
      }),
    });

    const responseData = await res.json();
    if (!res.ok) {
      throw new Error(responseData?.friendlyMessage || responseData?.error?.message || responseData?.message || 'Unable to complete social sign-in');
    }
    
    // Handle account linking — existing user with same email but different UID
    if (responseData.needsLinking && responseData.customToken) {
      // Sign out the temporary Apple user to prevent race condition
      await firebaseSignOut(auth);
      
      // Sign in as the original user using the custom token
      const originalResult = await signInWithCustomToken(auth, responseData.customToken);
      
      // Link Apple provider to the original account
      if (appleCredential) {
        try {
          await linkWithCredential(originalResult.user, appleCredential);
        } catch (linkErr) {
          console.warn('Could not link Apple credential:', linkErr.code || linkErr.message);
        }
      }
      
      try {
        await SessionService.sendLoginSession(originalResult.user);
      } catch (sessionError) {
        console.warn('Error sending login session:', sessionError);
      }
      
      return originalResult.user;
    }
    
    // Normal flow — no linking needed
    try {
      await SessionService.sendLoginSession(result.user);
    } catch (sessionError) {
      console.warn('Error sending login session:', sessionError);
    }
    
    return result.user;
  } catch (error) {
    if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') {
      return null;
    }
    throw error;
  }
};

// Email Registration
export const registerWithEmail = async (email, password, nickname) => {
  try {
    const res = await fetch(`${getApiBaseUrl()}/v4/auth/register/email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, nickname }),
    });

    if (!res.ok) {
      const errText = await res.text();
      let message = 'Registration failed';
      try {
        const parsed = JSON.parse(errText);
        message = parsed?.error?.message || parsed?.message || message;
      } catch { /* use default */ }
      throw new Error(message);
    }

    const data = await res.json();
    const customToken = typeof data === 'string' ? data : data.token || data;
    const { signInWithCustomToken } = await import('firebase/auth');
    const result = await signInWithCustomToken(auth, String(customToken));
    
    try {
      await SessionService.sendLoginSession(result.user);
    } catch (sessionError) {
      console.warn('Error sending login session:', sessionError);
    }
    
    return result.user;
  } catch (error) {
    throw error;
  }
};

// Forgot Password
export const forgotPassword = async (email) => {
  const res = await fetch(`${getApiBaseUrl()}/v1/profile/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });

  if (!res.ok) {
    const errText = await res.text();
    let message = 'Failed to send reset email';
    try {
      const parsed = JSON.parse(errText);
      message = parsed.message || parsed.error || message;
    } catch { /* use default */ }
    throw new Error(message);
  }
};

// Phone Verification (for non-phone users) - Send OTP
export const sendVerificationOtp = async (mobileWithPlus) => {
  const res = await fetch(`${getApiBaseUrl()}/v4/auth/otp/send`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mobile_with_plus: mobileWithPlus }),
  });
  if (!res.ok) throw new Error('Failed to send OTP');
};

// Phone Verification (for non-phone users) - Verify Phone
export const verifyPhoneNumber = async (mobileWithPlus, otp) => {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  
  const token = await user.getIdToken();
  const res = await fetch(`${getApiBaseUrl()}/v4/auth/verify-phone`, {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ mobile_with_plus: mobileWithPlus, otp }),
  });

  if (!res.ok) {
    const errText = await res.text();
    let message = 'Phone verification failed';
    try {
      const parsed = JSON.parse(errText);
      message = parsed?.error?.message || parsed?.message || message;
    } catch { /* use default */ }
    throw new Error(message);
  }
  return true;
};

// Check Profile - returns true if profile exists with nickname
export const checkProfile = async () => {
  try {
    const user = auth.currentUser;
    if (!user || user.isAnonymous) return false;

    const token = await user.getIdToken();
    const res = await fetch(`${getApiBaseUrl()}/v4/profile`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });

    if (!res.ok) return false;

    const data = await res.json();
    const profile = Array.isArray(data) ? data[0] : data?.data?.[0] || data;
    return !!(profile && profile.nickname);
  } catch {
    return false;
  }
};

// function to handle firebase errors
const handleFirebaseError = (error) => {
    switch (error.code) {
        case 'auth/invalid-verification-code':
            console.log('Invalid verification code');
        return 'Invalid verification code';
        case 'auth/missing-verification-code':
            console.log('Missing verification code');
        return 'Missing verification code';
        case 'auth/invalid-phone-number':
            console.log('Invalid phone number');
        return 'Invalid phone number';
        default:
            console.error('Error verifying OTP:', error);
        return error.message;
    }
    }
